/**
 * Котик для краш-игры.
 *
 * Рисуется кодом, а не спрайтами: фигура собирается из дуг и кривых, поэтому
 * остаётся резкой на любом разрешении, не требует загрузки картинок и
 * позволяет плавно смешивать позы. Спрайтовый лист пришлось бы готовить в
 * нескольких плотностях и всё равно получить ступеньки при повороте падения.
 *
 * Все размеры заданы в условных единицах относительно size, поэтому одного
 * набора пропорций хватает и для маленького котика на полосе истории, и для
 * крупного в центре экрана.
 */

export type CatPose = "run" | "fall" | "cheer" | "sit";

export interface CatPalette {
  fur: string;
  furShade: string;
  belly: string;
  ear: string;
  nose: string;
  stroke: string;
}

export const GINGER: CatPalette = {
  fur: "#ff9a6b",
  furShade: "#f97316",
  belly: "#ffd9c2",
  ear: "#ffbe9d",
  nose: "#ff6b8a",
  stroke: "#7c2d12",
};

export interface DrawCatOptions {
  x: number;
  y: number;
  /** Высота котика в пикселях: остальные размеры производные. */
  size: number;
  pose: CatPose;
  /** Время в миллисекундах для фазы анимации. */
  time: number;
  /** Поворот в радианах, используется при падении. */
  rotation?: number;
  /** Отражение по горизонтали. */
  flip?: boolean;
  palette?: CatPalette;
  /** Прозрачность: нужна для исчезающего следа. */
  alpha?: number;
}

/**
 * Рисует котика целиком. Порядок слоёв: хвост, дальние лапы, тело, ближние
 * лапы, голова — чтобы перекрытия выглядели естественно.
 */
export function drawCat(ctx: CanvasRenderingContext2D, options: DrawCatOptions): void {
  const {
    x,
    y,
    size,
    pose,
    time,
    rotation = 0,
    flip = false,
    palette = GINGER,
    alpha = 1,
  } = options;

  // Единица масштаба: все пропорции считаются от неё
  const u = size / 100;

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(x, y);
  if (rotation) ctx.rotate(rotation);
  if (flip) ctx.scale(-1, 1);

  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  const gait = getGait(pose, time);

  drawTail(ctx, u, palette, gait);
  drawLegs(ctx, u, palette, gait, "back");
  drawBody(ctx, u, palette, gait);
  drawLegs(ctx, u, palette, gait, "front");
  drawHead(ctx, u, palette, gait, pose);

  ctx.restore();
}

interface Gait {
  pose: CatPose;
  /** Подскок тела при беге. */
  bodyBob: number;
  /** Наклон корпуса. */
  bodyTilt: number;
  /** Фазы четырёх лап. */
  legs: [number, number, number, number];
  tailWave: number;
  headTilt: number;
  earTwitch: number;
  /** Насколько зажмурены глаза: 0 — открыты, 1 — закрыты. */
  squint: number;
}

function getGait(pose: CatPose, time: number): Gait {
  switch (pose) {
    case "run": {
      // Один цикл бега — 320 мс: достаточно быстро, чтобы читалось как бег,
      // и достаточно медленно, чтобы лапы не мерцали
      const phase = (time / 320) * Math.PI * 2;
      return {
        pose,
        bodyBob: Math.sin(phase * 2) * 2.5,
        bodyTilt: -0.06 + Math.sin(phase * 2) * 0.03,
        legs: [
          Math.sin(phase),
          Math.sin(phase + Math.PI),
          Math.sin(phase + Math.PI * 0.5),
          Math.sin(phase + Math.PI * 1.5),
        ],
        tailWave: Math.sin(phase * 0.8) * 0.5,
        headTilt: -0.08,
        earTwitch: Math.sin(phase * 2) * 0.06,
        squint: 0,
      };
    }

    case "fall": {
      // При падении лапы растопырены и мелко дрожат
      const phase = (time / 160) * Math.PI * 2;
      return {
        pose,
        bodyBob: 0,
        bodyTilt: 0.12,
        legs: [1.4, 1.5, -1.3, -1.4].map(
          (base, index) => base + Math.sin(phase + index) * 0.12,
        ) as [number, number, number, number],
        tailWave: Math.sin(phase * 1.6) * 1.1,
        headTilt: 0.22,
        earTwitch: -0.12,
        squint: 1,
      };
    }

    case "cheer": {
      const phase = (time / 420) * Math.PI * 2;
      return {
        pose,
        bodyBob: Math.abs(Math.sin(phase)) * 5,
        bodyTilt: 0,
        legs: [-0.9, -1.0, 0.3, 0.35],
        tailWave: Math.sin(phase * 2) * 0.8,
        headTilt: Math.sin(phase) * 0.1,
        earTwitch: Math.sin(phase * 3) * 0.1,
        squint: 0.45,
      };
    }

    case "sit":
    default: {
      const phase = (time / 1800) * Math.PI * 2;
      return {
        pose,
        bodyBob: Math.sin(phase) * 0.8,
        bodyTilt: 0,
        legs: [0.1, 0.12, 0, 0],
        tailWave: Math.sin(phase) * 0.35,
        headTilt: 0,
        earTwitch: 0,
        squint: 0,
      };
    }
  }
}

function drawBody(
  ctx: CanvasRenderingContext2D,
  u: number,
  palette: CatPalette,
  gait: Gait,
): void {
  ctx.save();
  ctx.translate(0, gait.bodyBob * u);
  ctx.rotate(gait.bodyTilt);

  // Корпус
  ctx.beginPath();
  ctx.ellipse(0, 0, 34 * u, 23 * u, 0, 0, Math.PI * 2);
  ctx.fillStyle = palette.fur;
  ctx.fill();
  ctx.lineWidth = 2.5 * u;
  ctx.strokeStyle = palette.stroke;
  ctx.stroke();

  // Живот светлее: даёт объём без градиентов, которые дороже в отрисовке
  ctx.beginPath();
  ctx.ellipse(2 * u, 8 * u, 22 * u, 12 * u, 0, 0, Math.PI * 2);
  ctx.fillStyle = palette.belly;
  ctx.fill();

  // Полоски на спине
  ctx.strokeStyle = palette.furShade;
  ctx.lineWidth = 3 * u;
  for (let i = -1; i <= 1; i += 1) {
    ctx.beginPath();
    ctx.moveTo(i * 12 * u - 4 * u, -18 * u);
    ctx.quadraticCurveTo(i * 12 * u, -12 * u, i * 12 * u + 3 * u, -8 * u);
    ctx.stroke();
  }

  ctx.restore();
}

function drawLegs(
  ctx: CanvasRenderingContext2D,
  u: number,
  palette: CatPalette,
  gait: Gait,
  layer: "front" | "back",
): void {
  // Задние лапы рисуются темнее и позади корпуса — так читается глубина
  const isFront = layer === "front";
  const color = isFront ? palette.fur : palette.furShade;
  const [frontA, frontB, backA, backB] = gait.legs;

  const pairs: { x: number; swing: number }[] = isFront
    ? [
        { x: 20 * u, swing: frontA },
        { x: 26 * u, swing: frontB },
      ]
    : [
        { x: -20 * u, swing: backA },
        { x: -26 * u, swing: backB },
      ];

  ctx.save();
  ctx.translate(0, gait.bodyBob * u);

  for (const { x, swing } of pairs) {
    const isFalling = gait.pose === "fall";
    // При беге лапа качается вдоль корпуса, при падении расходится в стороны
    const dx = isFalling ? swing * 16 * u : swing * 11 * u;
    const dy = isFalling ? 14 * u - Math.abs(swing) * 4 * u : 20 * u - Math.abs(swing) * 3 * u;

    ctx.beginPath();
    ctx.moveTo(x, 12 * u);
    ctx.quadraticCurveTo(x + dx * 0.6, 18 * u, x + dx, dy + 4 * u);
    ctx.strokeStyle = color;
    ctx.lineWidth = 7 * u;
    ctx.stroke();

    // Лапка
    ctx.beginPath();
    ctx.ellipse(x + dx, dy + 6 * u, 5.5 * u, 4 * u, 0, 0, Math.PI * 2);
    ctx.fillStyle = palette.belly;
    ctx.fill();
    ctx.lineWidth = 2 * u;
    ctx.strokeStyle = palette.stroke;
    ctx.stroke();
  }

  ctx.restore();
}

function drawTail(
  ctx: CanvasRenderingContext2D,
  u: number,
  palette: CatPalette,
  gait: Gait,
): void {
  ctx.save();
  ctx.translate(-30 * u, gait.bodyBob * u - 4 * u);

  const wave = gait.tailWave;

  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.bezierCurveTo(
    -16 * u,
    -6 * u + wave * 8 * u,
    -26 * u,
    -22 * u + wave * 14 * u,
    -18 * u,
    -34 * u + wave * 10 * u,
  );
  ctx.strokeStyle = palette.furShade;
  ctx.lineWidth = 8 * u;
  ctx.stroke();

  // Светлый кончик хвоста
  ctx.beginPath();
  ctx.arc(-18 * u, -34 * u + wave * 10 * u, 4.5 * u, 0, Math.PI * 2);
  ctx.fillStyle = palette.belly;
  ctx.fill();

  ctx.restore();
}

function drawHead(
  ctx: CanvasRenderingContext2D,
  u: number,
  palette: CatPalette,
  gait: Gait,
  pose: CatPose,
): void {
  ctx.save();
  ctx.translate(28 * u, gait.bodyBob * u - 18 * u);
  ctx.rotate(gait.headTilt);

  // Уши — за головой, чтобы основание было прикрыто
  for (const side of [-1, 1] as const) {
    ctx.beginPath();
    const baseX = side === 1 ? 11 * u : -9 * u;
    const twitch = gait.earTwitch * side * 10 * u;
    ctx.moveTo(baseX - 6 * u, -12 * u);
    ctx.lineTo(baseX + 2 * u, -26 * u + twitch);
    ctx.lineTo(baseX + 10 * u, -11 * u);
    ctx.closePath();
    ctx.fillStyle = palette.fur;
    ctx.fill();
    ctx.lineWidth = 2.5 * u;
    ctx.strokeStyle = palette.stroke;
    ctx.stroke();

    // Внутренняя часть уха
    ctx.beginPath();
    ctx.moveTo(baseX - 1 * u, -13 * u);
    ctx.lineTo(baseX + 2.5 * u, -21 * u + twitch);
    ctx.lineTo(baseX + 6 * u, -13 * u);
    ctx.closePath();
    ctx.fillStyle = palette.ear;
    ctx.fill();
  }

  // Голова
  ctx.beginPath();
  ctx.arc(0, 0, 19 * u, 0, Math.PI * 2);
  ctx.fillStyle = palette.fur;
  ctx.fill();
  ctx.lineWidth = 2.5 * u;
  ctx.strokeStyle = palette.stroke;
  ctx.stroke();

  // Морда
  ctx.beginPath();
  ctx.ellipse(4 * u, 6 * u, 12 * u, 9 * u, 0, 0, Math.PI * 2);
  ctx.fillStyle = palette.belly;
  ctx.fill();

  drawEyes(ctx, u, palette, gait, pose);

  // Нос
  ctx.beginPath();
  ctx.moveTo(6 * u, 3 * u);
  ctx.lineTo(11 * u, 3 * u);
  ctx.lineTo(8.5 * u, 7 * u);
  ctx.closePath();
  ctx.fillStyle = palette.nose;
  ctx.fill();

  // Рот: при падении открыт от испуга
  ctx.beginPath();
  ctx.strokeStyle = palette.stroke;
  ctx.lineWidth = 1.8 * u;
  if (pose === "fall") {
    ctx.ellipse(8.5 * u, 12 * u, 4 * u, 5 * u, 0, 0, Math.PI * 2);
    ctx.fillStyle = palette.stroke;
    ctx.fill();
  } else if (pose === "cheer") {
    ctx.arc(8.5 * u, 8 * u, 5 * u, 0.15 * Math.PI, 0.85 * Math.PI);
    ctx.stroke();
  } else {
    ctx.moveTo(8.5 * u, 8 * u);
    ctx.quadraticCurveTo(5 * u, 12 * u, 2 * u, 9 * u);
    ctx.moveTo(8.5 * u, 8 * u);
    ctx.quadraticCurveTo(12 * u, 12 * u, 15 * u, 9 * u);
    ctx.stroke();
  }

  // Усы
  ctx.strokeStyle = "rgba(124, 45, 18, 0.55)";
  ctx.lineWidth = 1.4 * u;
  for (const offset of [-2, 2]) {
    ctx.beginPath();
    ctx.moveTo(12 * u, (5 + offset) * u);
    ctx.lineTo(26 * u, (2 + offset * 1.6) * u);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-2 * u, (6 + offset) * u);
    ctx.lineTo(-15 * u, (3 + offset * 1.6) * u);
    ctx.stroke();
  }

  ctx.restore();
}

function drawEyes(
  ctx: CanvasRenderingContext2D,
  u: number,
  palette: CatPalette,
  gait: Gait,
  pose: CatPose,
): void {
  const eyes: { x: number; y: number }[] = [
    { x: -5 * u, y: -3 * u },
    { x: 10 * u, y: -4 * u },
  ];

  for (const eye of eyes) {
    if (pose === "fall") {
      // Зажмуренные глаза-крестики: мгновенно читаемый знак неудачи
      ctx.strokeStyle = palette.stroke;
      ctx.lineWidth = 2.6 * u;
      ctx.beginPath();
      ctx.moveTo(eye.x - 3.5 * u, eye.y - 3.5 * u);
      ctx.lineTo(eye.x + 3.5 * u, eye.y + 3.5 * u);
      ctx.moveTo(eye.x + 3.5 * u, eye.y - 3.5 * u);
      ctx.lineTo(eye.x - 3.5 * u, eye.y + 3.5 * u);
      ctx.stroke();
      continue;
    }

    if (gait.squint > 0.3) {
      ctx.strokeStyle = palette.stroke;
      ctx.lineWidth = 2.4 * u;
      ctx.beginPath();
      ctx.arc(eye.x, eye.y + 1 * u, 4 * u, Math.PI * 1.15, Math.PI * 1.85);
      ctx.stroke();
      continue;
    }

    ctx.beginPath();
    ctx.ellipse(eye.x, eye.y, 4.2 * u, 5 * u, 0, 0, Math.PI * 2);
    ctx.fillStyle = "#1c1233";
    ctx.fill();

    // Блик оживляет взгляд и стоит один вызов отрисовки
    ctx.beginPath();
    ctx.arc(eye.x + 1.4 * u, eye.y - 1.6 * u, 1.5 * u, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.fill();
  }
}
